import { createSlice } from "@reduxjs/toolkit";
import { RootState } from "../../../app/store";
import { fetchUserActionsStatusThunk, updateMultipleIRReviewStatusesThunk } from "./userActionsStatusThunk";
import {
    ReviewStatusEnum,
    ReviewStatus,
    ReportReviewPayload,
} from "../../../api/UserActionsStatus";
import { actionTypeToStateKey, mapApiToUi } from "../utils/reviewerMappers";

export type LoadStatus = "idle" | "loading" | "succeeded" | "failed" | "saving";

export interface UserActionsStatusData {
    ir_l1_review?: ReportReviewPayload,
    ir_l2_review?: ReportReviewPayload,
    ir_l3_review?: ReportReviewPayload
}

export interface UserActionsStatusState {
    status: LoadStatus;
    error: string | null;
    data: UserActionsStatusData | null;
    caseId: string | null;
}

export const initialUserActionsStatusState: UserActionsStatusState = {
    status: "idle",
    error: null,
    data: null,
    caseId: null,
};

export const getDefaultReviewStatus = (): ReviewStatus => ({
    status: null,
    assigned_by: null,
    assigned_at: null,
    approver: null,
    approved_at: null,
    skipped_by: null,
    skipped_at: null,
    comment: null
});

const userActionsStatusSlice = createSlice({
    name: "userActionsStatus",
    initialState: initialUserActionsStatusState,
    reducers: {
        clearUserActionsStatus: () => initialUserActionsStatusState,
        resetUserActionsError: (state) => { state.error = null; },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchUserActionsStatusThunk.pending, (state) => {
                state.status = "loading";
                state.error = null;
                state.data = null;   
            })
            .addCase(fetchUserActionsStatusThunk.fulfilled, (state, action) => {
                state.status = "succeeded";
                state.data = mapApiToUi(action.payload);
                state.caseId = action.meta.arg.case_id;
                state.error = null;
            })
            .addCase(fetchUserActionsStatusThunk.rejected, (state, action) => {
                state.status = "failed";
                state.error = (action.payload as string) || "Failed to fetch user actions status";
            })
            .addCase(updateMultipleIRReviewStatusesThunk.pending, (state) => {
                state.status = "saving"; state.error = null;
            })
            .addCase(updateMultipleIRReviewStatusesThunk.fulfilled, (state, action) => {
                state.status = "succeeded";
                state.error = null;
                if (!state.data) state.data = {};

                for (const { action_type, review_status } of action.payload.reviewers) {
                    const stateKey = actionTypeToStateKey[action_type];
                    state.data[stateKey] = { action_type, review_status };
                }
                
            })
            .addCase(updateMultipleIRReviewStatusesThunk.rejected, (state, action) => {
                state.status = "failed";
                state.error = (action.payload as string) || "Failed to update reviewers";
            });
    },
});

export const { clearUserActionsStatus, resetUserActionsError } = userActionsStatusSlice.actions;
export const userActionStatusReducer = userActionsStatusSlice.reducer;

// --- Selectors ---
export const selectUserActionsStatusState = (state: RootState): UserActionsStatusState => state.userActionsStatusData;
export const selectUserActionsStatus = (state: RootState): UserActionsStatusData | null => state.userActionsStatusData.data;
export const selectUserActionsStatusSaving = (state: RootState): boolean => state.userActionsStatusData.status === "saving";

export const selectIsIRFinalized = (state: RootState): boolean => {
    const l1Review = state.userActionsStatusData.data?.ir_l1_review;
    return l1Review?.review_status?.status === ReviewStatusEnum.APPROVED;
};

export const selectIRActiveReviews = (state: RootState) => {
    const data = state.userActionsStatusData.data;
    if (!data) return [];

    return [data.ir_l1_review, data.ir_l2_review, data.ir_l3_review].filter(
        (reviewStatus): reviewStatus is NonNullable<typeof reviewStatus> =>
          reviewStatus !== undefined && reviewStatus !== null
      );
};
